// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { checkIfUrl } from "@werner/XDL/Utilities";
import React, { useEffect, useRef, useState } from "react";
import { useZxing } from "react-zxing";
import { css } from "styled-components";

interface BarcodeScannerProps {
    isOpen: boolean;
    onChange: (text: string, isUrl?: boolean) => void;
    onError: (errorMessage: any) => void;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({ isOpen, onChange, onError }) => {
    const streamRef = useRef<MediaStream | null>(null);
    const lastScannedRef = useRef<string | null>(null);
    const [hasScanned, setHasScanned] = useState(false);

    const { ref } = useZxing({
        onDecodeResult: result => {
            const decodedText = result.getText();
            setHasScanned(true);
            if (decodedText === lastScannedRef.current) {
                return;
            }
            lastScannedRef.current = decodedText;
            onChange(decodedText, checkIfUrl(decodedText));

            if (navigator.vibrate) {
                navigator.vibrate(200);
            }
        },
        onError: error => {
            onError(error);
        },
        onDecodeError: error => {
            setHasScanned(false);
            if (error.name !== "NotFoundException") {
                onError(error);
            }
        },
        constraints: {
            video: {
                facingMode: { ideal: "environment" },
                width: { ideal: 1280 },
                height: { ideal: 720 },
                aspectRatio: { ideal: 16 / 9 },
            },
        },
        paused: !isOpen, // Pause camera if modal is closed
    });

    useEffect(() => {
        if (!isOpen && streamRef.current) {
            // Stop the camera when modal is closed
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
            lastScannedRef.current = null;
        }
    }, [isOpen]);

    return (
        <StyledWrapper
            {...{
                css: css`
                    position: relative;
                    border: ${hasScanned ? `4px solid #3AA248` : `4px solid transparent`};
                    border-radius: 8px;
                    transition: border 0.3s ease;
                `,
            }}
        >
            <video
                ref={ref}
                autoPlay
                playsInline
                muted
                style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "240px",
                    display: "block",
                    objectFit: "cover",
                }}
            />
        </StyledWrapper>
    );
};

export default BarcodeScanner;
